import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { GakEak } from "../utils/caseFormat";
import { 
  FolderOpen, Clock, AlertTriangle, Calendar, ArrowRight, FileText,
  Scale, Building2, CalendarX, CalendarCheck, Gavel, Printer,
  FileCheck, ClipboardList, Truck, Package
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { WorkspaceGate, WorkspaceBadge } from "../components/Workspace";
import { useWorkspace, branchDisplay } from "../utils/workspace";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

const PAGE = 5;

/** Κάτω μέρος λίστας: «+5 ακόμη» / «Όλα (N)» — user Νοέ 2026 */
const LoadMoreFooter = ({ shown, total, onMore, onAll, testId }) => {
  if (total <= shown) return total > PAGE ? <p className="text-xs text-slate-400 text-center pt-2" data-testid={`${testId}-all-shown`}>Εμφανίζονται όλα ({total})</p> : null;
  return (
    <div className="flex items-center justify-center gap-2 pt-3 border-t border-slate-100 mt-2" data-testid={`${testId}-footer`}>
      <span className="text-xs text-slate-500">{shown} από {total}</span>
      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onMore} data-testid={`${testId}-more-btn`}>+{Math.min(PAGE, total - shown)} ακόμη</Button>
      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onAll} data-testid={`${testId}-all-btn`}>Όλα ({total})</Button>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [upcomingHearings, setUpcomingHearings] = useState([]);
  const [upcomingPinakia, setUpcomingPinakia] = useState([]);
  const [supervisorStats, setSupervisorStats] = useState(null);
  const [subDeptData, setSubDeptData] = useState(null);
  const [activeSubDept, setActiveSubDept] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter109: Χώρος Εργασίας (Κατάστημα + Τμήμα) — τα δεδομένα φορτώνουν ΜΟΝΟ μετά την επιβεβαίωση
  const { ws, save: saveWs } = useWorkspace(user);
  const [editingWs, setEditingWs] = useState(false);
  const [courtFilter, setCourtFilter] = useState(ws?.court_type || "");
  const branch = ws?.branch || "";
  const year = ws?.year || "";
  // user (Νοέ 2026): αρχικά 5 αποτελέσματα ανά πεδίο, με «+5» / «Όλα» στο κάτω μέρος
  const [visible, setVisible] = useState({ hearings: PAGE, pinakia: PAGE, deadlines: PAGE });
  const showMore = (key, all, total) => setVisible((v) => ({ ...v, [key]: all ? total : Math.min(total, v[key] + PAGE) }));

  const userCourtTypes = user?.court_types || ["Μονομελές", "Πολυμελές"];
  const canSeeBoth = userCourtTypes.includes("Μονομελές") && userCourtTypes.includes("Πολυμελές");

  const isSupervisor = user?.role === "admin" || user?.role === "katastima";

  useEffect(() => { setCourtFilter(ws?.court_type || ""); }, [ws?.court_type]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (ws) fetchData(); }, [courtFilter, branch, year, !!ws]);

  const fetchData = async () => {
    setLoading(true);
    setVisible({ hearings: PAGE, pinakia: PAGE, deadlines: PAGE });
    // Progressive rendering: μην μπλοκάρεις περιμένοντας τα πάντα.
    // Κάθε section φορτώνει ανεξάρτητα — αν ένα αργεί, τα άλλα φαίνονται ήδη.
    const br = (branch ? `&branch=${encodeURIComponent(branch)}` : "") + (year ? `&year=${encodeURIComponent(year)}` : "");
    const ct = (courtFilter ? `&court_type=${encodeURIComponent(courtFilter)}` : "") + br;

    const loadSection = async (loaderFn, setterFn, errLabel) => {
      try {
        const res = await loaderFn();
        setterFn(res);
      } catch (e) {
        console.warn(`Dashboard: ${errLabel} failed`, e?.response?.status || e?.message || e);
      }
    };

    // Όλα τρέχουν παράλληλα — αλλά καθένα γράφει state μόλις ολοκληρωθεί
    loadSection(
      () => axios.get(`${API}/stats/dashboard?court_type=${encodeURIComponent(courtFilter)}${br}`).then(r => r.data),
      setStats, "stats"
    );
    loadSection(
      () => axios.get(`${API}/deadlines?days=14${ct}`).then(r => (r.data || []).slice(0, 100)),
      setDeadlines, "deadlines"
    );
    loadSection(
      () => axios.get(`${API}/dashboard/upcoming-hearings?limit=50${ct}`).then(r => r.data || []),
      setUpcomingHearings, "upcoming-hearings"
    );
    loadSection(
      () => axios.get(`${API}/dashboard/upcoming-pinakia?limit=50${ct}`).then(r => r.data || []),
      setUpcomingPinakia, "upcoming-pinakia"
    );
    if (isSupervisor) {
      Promise.allSettled([
        axios.get(`${API}/supervisor/overview?${ct.replace(/^&/, "")}`).then(r => setSupervisorStats(r.data)).catch(e => console.warn("supervisor overview failed", e?.response?.status || e?.message)),
        axios.get(`${API}/supervisor/sub-departments?${ct.replace(/^&/, "")}`).then(r => setSubDeptData(r.data)).catch(e => console.warn("sub-departments failed", e?.response?.status || e?.message)),
      ]);
    }

    // Master loading flag: γίνεται false αμέσως ώστε η σελίδα να φανεί
    // με placeholder values (0) μέχρι να φορτώσει κάθε section ασύγχρονα.
    setTimeout(() => setLoading(false), 50);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("el-GR", { weekday: "short", day: "numeric", month: "short" });
  };

  const getUrgencyBadge = (urgency) => {
    switch (urgency) {
      case "urgent": return <Badge className="badge-urgent">Επείγον</Badge>;
      case "warning": return <Badge className="badge-warning">Προσοχή</Badge>;
      default: return <Badge className="badge-info">Κανονικό</Badge>;
    }
  };

  // ΣΗΜΕΙΩΣΗ: Καταργήθηκε το blocking `if (loading) return ...` ώστε η σελίδα
  // να εμφανίζεται άμεσα και κάθε section να φορτώνει ανεξάρτητα στο παρασκήνιο.
  // Κάθε card χρησιμοποιεί ήδη optional chaining (`stats?.x || 0`) ώστε να
  // δείχνει «0» κατά τη φόρτωση και να ενημερώνεται με τις πραγματικές τιμές.

  const hearingPercent = stats?.total_cases > 0 ? Math.round((stats?.cases_with_hearing / stats?.total_cases) * 100) : 0;

  const hearingChartData = (stats?.hearing_by_subject || []).slice(0, 8).map(s => ({
    subject: (s.subject || "Χωρίς αντικείμενο").length > 20
      ? (s.subject || "Χωρίς αντικείμενο").substring(0, 20) + "..."
      : (s.subject || "Χωρίς αντικείμενο"),
    fullSubject: s.subject || "Χωρίς αντικείμενο",
    "Με δικάσιμο": s.with_hearing,
    "Χωρίς δικάσιμο": s.without_hearing
  }));

  if (!ws || editingWs) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Πίνακας Ελέγχου</h1>
          <p className="text-slate-500 mt-1">Επιλέξτε Κατάστημα, Τμήμα και Έτος για να φορτωθούν τα δεδομένα</p>
        </div>
        <WorkspaceGate user={user} initial={ws} onConfirm={(next) => { saveWs(next); setCourtFilter(next.court_type || ""); setEditingWs(false); }} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Πίνακας Ελέγχου</h1>
          <p className="text-slate-500 mt-1">Επισκόπηση του συστήματος διαχείρισης</p>
        </div>
        <div className="flex items-center gap-3">
          <WorkspaceBadge ws={ws} onChange={() => setEditingWs(true)} />
          {/* Court Type Filter */}
          {canSeeBoth && (
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="court-type-filter">
              <button
                onClick={() => setCourtFilter("")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${!courtFilter ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="filter-all"
              >Όλα</button>
              <button
                onClick={() => setCourtFilter("Μονομελές")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${courtFilter === "Μονομελές" ? "bg-blue-600 text-white shadow" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="filter-mono"
              >Μονομελές</button>
              <button
                onClick={() => setCourtFilter("Πολυμελές")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${courtFilter === "Πολυμελές" ? "bg-purple-600 text-white shadow" : "text-slate-500 hover:text-slate-700"}`}
                data-testid="filter-poly"
              >Πολυμελές</button>
            </div>
          )}
          <Link to="/import">
            <Button data-testid="quick-import-btn"><FileText className="w-4 h-4 mr-2" />Εισαγωγή</Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid - Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/cases" data-testid="stat-total-cases">
          <Card className="stat-card hover:ring-2 hover:ring-blue-200 cursor-pointer transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Συνολικές Υποθέσεις</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_cases || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FolderOpen className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-4 text-sm">
                <span className="text-emerald-600">{stats?.active_cases || 0} ενεργές</span>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/today" data-testid="stat-today-deadlines">
          <Card className="stat-card hover:ring-2 hover:ring-amber-200 cursor-pointer transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Σημερινές Προθεσμίες</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.today_deadlines || 0}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <div className="mt-4 text-sm text-slate-500">{stats?.week_deadlines || 0} αυτή την εβδομάδα</div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/today" data-testid="stat-overdue">
          <Card className="stat-card hover:ring-2 hover:ring-red-200 cursor-pointer transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Εκπρόθεσμες</p>
                  <p className="text-3xl font-bold text-red-600 mt-1">{stats?.overdue_deadlines || 0}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div className="mt-4 text-sm text-red-500">Απαιτείται άμεση δράση</div>
            </CardContent>
          </Card>
        </Link>

        <Card className="stat-card" data-testid="stat-court-types">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Ανά Τύπο Δικαστηρίου</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-bold text-slate-900">{stats?.mono_cases || 0}</span>
                  <span className="text-slate-400">/</span>
                  <span className="text-2xl font-bold text-slate-900">{stats?.poly_cases || 0}</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3 text-sm">
              {canSeeBoth ? (
                <>
                  <button onClick={() => setCourtFilter("Μονομελές")} className="text-blue-600 hover:underline cursor-pointer" data-testid="court-mono-link">Μονομελές</button>
                  <button onClick={() => setCourtFilter("Πολυμελές")} className="text-purple-600 hover:underline cursor-pointer" data-testid="court-poly-link">Πολυμελές</button>
                </>
              ) : (
                <span className="text-blue-600">{userCourtTypes[0]}</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Supervisor Overview */}
      {isSupervisor && supervisorStats && (
        <Card className="border-l-4 border-l-indigo-500" data-testid="supervisor-overview">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-indigo-600" />
              Πίνακας Προϊσταμένου — {branchDisplay(supervisorStats.court_branch || branch)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-4">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-800">{supervisorStats.ready_dispatch}</p>
                <p className="text-xs text-slate-500 mt-1">Έτοιμες Διακίνησης</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{supervisorStats.dispatched}</p>
                <p className="text-xs text-slate-500 mt-1">Διακινημένες</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-600">{supervisorStats.pending_pretrial}</p>
                <p className="text-xs text-slate-500 mt-1">Εκκρεμείς Προδικαστικοί</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{supervisorStats.overdue_deadlines}</p>
                <p className="text-xs text-slate-500 mt-1">Εκπρόθεσμες</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{supervisorStats.upcoming_7_days}</p>
                <p className="text-xs text-slate-500 mt-1">Δικάσιμοι 7 ημερών</p>
              </div>
            </div>

            {/* 3 Sub-Department Icons */}
            {subDeptData && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <button onClick={() => setActiveSubDept(activeSubDept === "proposals" ? null : "proposals")}
                    className={`p-4 rounded-xl border-2 transition-all text-center hover:shadow-md ${activeSubDept === "proposals" ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white"}`}
                    data-testid="subdept-proposals">
                    <FileCheck className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                    <p className="font-semibold text-sm">Προκατάθεση</p>
                    <p className="text-xs text-slate-500">Προτάσεων / Αντικρούσεων / Αρθ.269</p>
                    <p className="text-2xl font-bold text-blue-700 mt-1">{subDeptData.proposals.count}</p>
                  </button>
                  <button onClick={() => setActiveSubDept(activeSubDept === "pretrial" ? null : "pretrial")}
                    className={`p-4 rounded-xl border-2 transition-all text-center hover:shadow-md ${activeSubDept === "pretrial" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white"}`}
                    data-testid="subdept-pretrial">
                    <ClipboardList className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
                    <p className="font-semibold text-sm">Προδικαστικός</p>
                    <p className="text-xs text-slate-500">Έλεγχος</p>
                    <p className="text-2xl font-bold text-emerald-700 mt-1">{subDeptData.pretrial.count}</p>
                  </button>
                  <button onClick={() => setActiveSubDept(activeSubDept === "dispatch" ? null : "dispatch")}
                    className={`p-4 rounded-xl border-2 transition-all text-center hover:shadow-md ${activeSubDept === "dispatch" ? "border-amber-500 bg-amber-50" : "border-slate-200 bg-white"}`}
                    data-testid="subdept-dispatch">
                    <Truck className="w-8 h-8 mx-auto mb-2 text-amber-600" />
                    <p className="font-semibold text-sm">Διακίνηση</p>
                    <p className="text-xs text-slate-500">Φάκελοι: {subDeptData.total_folders} | BOX: {subDeptData.total_box}</p>
                    <p className="text-2xl font-bold text-amber-700 mt-1">{subDeptData.dispatch.count}</p>
                  </button>
                </div>

                {/* Sub-department detail panel */}
                {activeSubDept && subDeptData[activeSubDept]?.cases?.length > 0 && (
                  <div className="mt-3 border rounded-lg overflow-hidden">
                    <div className="max-h-[300px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>ΓΑΚ/ΕΑΚ</TableHead>
                            <TableHead>Τύπος</TableHead>
                            <TableHead>Αντικείμενο</TableHead>
                            {activeSubDept === "proposals" && <TableHead>Στάδιο</TableHead>}
                            {activeSubDept === "pretrial" && <TableHead>Κατάσταση</TableHead>}
                            {activeSubDept === "dispatch" && <TableHead>Διακίνηση</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {subDeptData[activeSubDept].cases.map(c => (
                            <TableRow key={c.id}>
                              <TableCell className="font-mono text-sm">
                                <Link to={`/cases/${c.id}`} className="hover:text-blue-600">
                                  <GakEak caseObj={c} />
                                  {c.eak_number && <span className="text-indigo-600 ml-1">({c.eak_number})</span>}
                                </Link>
                              </TableCell>
                              <TableCell><Badge variant="outline" className="text-xs">{c.court_type}</Badge></TableCell>
                              <TableCell className="text-sm max-w-[120px] truncate">{c.subject || "-"}</TableCell>
                              {activeSubDept === "proposals" && (
                                <TableCell>
                                  {(c.deadlines || []).filter(d => ["proposals","addition","late_claims","late_counter","filing_269"].includes(d.type) && d.status === "pending")
                                    .map((d) => <Badge key={`${c.id}-${d.type}`} variant="outline" className="text-xs mr-1">{d.title?.split("(")[0]}</Badge>)}
                                </TableCell>
                              )}
                              {activeSubDept === "pretrial" && (
                                <TableCell>
                                  <Badge className={c.pretrial_status === "formal_review" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}>
                                    {c.pretrial_status === "formal_review" ? "Τυπικός Έλεγχος" : c.pretrial_status === "in_progress" ? "Σε εξέλιξη" : c.pretrial_status || "Εκκρεμεί"}
                                  </Badge>
                                </TableCell>
                              )}
                              {activeSubDept === "dispatch" && (
                                <TableCell>
                                  <Badge className={c.dispatch_status === "dispatched" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}>
                                    {c.dispatch_status === "dispatched" ? "Απεστάλη" : "Έτοιμη"}
                                  </Badge>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Hearing Date Stats & Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card data-testid="stat-hearing-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              Προσδιορισμός Δικασίμου
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarCheck className="w-5 h-5 text-emerald-500" />
                <span className="text-sm text-slate-600">Με δικάσιμο</span>
              </div>
              <span className="text-lg font-bold text-emerald-600">{stats?.cases_with_hearing || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarX className="w-5 h-5 text-red-500" />
                <span className="text-sm text-slate-600">Χωρίς δικάσιμο</span>
              </div>
              <span className="text-lg font-bold text-red-600">{stats?.cases_without_hearing || 0}</span>
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-slate-500">
                <span>Ποσοστό προσδιορισμού</span>
                <span className="font-medium">{hearingPercent}%</span>
              </div>
              <Progress value={hearingPercent} className="h-2" />
            </div>
            <Link to="/hearings">
              <Button variant="outline" size="sm" className="w-full mt-2" data-testid="view-hearings-btn">
                Προβολή Πινακίων
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2" data-testid="stat-hearing-by-subject">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Scale className="w-4 h-4 text-indigo-600" />
              Δικάσιμος ανά Αντικείμενο
            </CardTitle>
            <CardDescription>Κατανομή υποθέσεων με/χωρίς δικάσιμο</CardDescription>
          </CardHeader>
          <CardContent>
            {hearingChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={hearingChartData} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="subject" tick={{ fontSize: 11 }} width={140} interval={0} />
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                    formatter={(value, name) => [value, name]}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullSubject || label}
                  />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Bar dataKey="Με δικάσιμο" stackId="a" fill="#059669" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Χωρίς δικάσιμο" stackId="a" fill="#dc2626" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-8 text-slate-400">Δεν υπάρχουν δεδομένα</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Hearings & Pinakia */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Upcoming Hearings */}
        <Card data-testid="upcoming-hearings-card">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-600" />
                Προσεχείς Δικάσιμοι
              </CardTitle>
              <CardDescription>Οι επόμενες δικάσιμοι{year ? ` · έτος ${year}` : ""}</CardDescription>
            </div>
            <Link to="/hearings">
              <Button variant="ghost" size="sm" data-testid="view-all-hearings-btn">
                Όλες<ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {upcomingHearings.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-sm">Δεν υπάρχουν προσεχείς δικάσιμοι</div>
            ) : (
              <div className="space-y-2">
                {upcomingHearings.slice(0, visible.hearings).map((h, i) => (
                  <Link key={h.id || i} to={`/cases/${h.id}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-emerald-50 transition-colors"
                    data-testid={`upcoming-hearing-${i}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-semibold text-slate-800">
                          <GakEak caseObj={h} />
                        </span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{h.court_type}</Badge>
                      </div>
                      {h.subject && <p className="text-xs text-slate-500 truncate mt-0.5">{h.subject}</p>}
                    </div>
                    <div className="text-right ml-3">
                      <p className="text-sm font-medium text-emerald-700">{formatDate(h.hearing_date_parsed)}</p>
                    </div>
                  </Link>
                ))}
                <LoadMoreFooter shown={Math.min(visible.hearings, upcomingHearings.length)} total={upcomingHearings.length} testId="upcoming-hearings"
                  onMore={() => showMore("hearings", false, upcomingHearings.length)} onAll={() => showMore("hearings", true, upcomingHearings.length)} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Pinakia */}
        <Card data-testid="upcoming-pinakia-card">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Gavel className="w-4 h-4 text-indigo-600" />
                Επόμενα Πινάκια
              </CardTitle>
              <CardDescription>Τα προσεχή πινάκια{year ? ` · έτος ${year}` : ""}</CardDescription>
            </div>
            <Link to="/hearings">
              <Button variant="ghost" size="sm" data-testid="view-all-pinakia-btn">
                Όλα<ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {upcomingPinakia.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-sm">Δεν υπάρχουν προσεχή πινάκια</div>
            ) : (
              <div className="space-y-2">
                {upcomingPinakia.slice(0, visible.pinakia).map((p, i) => (
                  <Link key={`pin-${p.label || ''}-${p.hearing_date || ''}-${i}`} to="/hearings"
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-indigo-50 transition-colors"
                    data-testid={`upcoming-pinakio-${i}`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-semibold text-slate-800">{p.label}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{p.court_type}</Badge>
                        <span className="text-xs text-slate-500">{p.case_count} υποθ.</span>
                      </div>
                    </div>
                    <div className="text-right ml-3">
                      <p className="text-sm font-medium text-indigo-700">{formatDate(p.date)}</p>
                    </div>
                  </Link>
                ))}
                <LoadMoreFooter shown={Math.min(visible.pinakia, upcomingPinakia.length)} total={upcomingPinakia.length} testId="upcoming-pinakia"
                  onMore={() => showMore("pinakia", false, upcomingPinakia.length)} onAll={() => showMore("pinakia", true, upcomingPinakia.length)} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Deadlines */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Προσεχείς Προθεσμίες</CardTitle>
            <CardDescription>Οι επόμενες 14 ημέρες{year ? ` · έτος ${year}` : ""}</CardDescription>
          </div>
          <Link to="/calendar">
            <Button variant="ghost" size="sm" data-testid="view-calendar-btn">
              Ημερολόγιο<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {deadlines.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Δεν υπάρχουν προσεχείς προθεσμίες</p>
            </div>
          ) : (
            <div className="space-y-3">
              {deadlines.slice(0, visible.deadlines).map((deadline, index) => (
                <Link key={`${deadline.case_id}-${deadline.deadline_type}-${index}`}
                  to={`/cases/${deadline.case_id}`}
                  className={`block p-4 rounded-lg transition-colors ${
                    deadline.urgency === "urgent" ? "deadline-urgent hover:bg-red-100" :
                    deadline.urgency === "warning" ? "deadline-warning hover:bg-amber-100" :
                    "deadline-normal hover:bg-blue-100"
                  }`} data-testid={`deadline-item-${index}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm font-semibold text-slate-900">
                          <GakEak caseObj={deadline} />
                        </span>
                        <Badge className={deadline.court_type === "Μονομελές" ? "court-mono" : "court-poly"}>
                          {deadline.court_type}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-700">{deadline.deadline_title}</p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      {getUrgencyBadge(deadline.urgency)}
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-900">{formatDate(deadline.deadline_date)}</p>
                        <p className="text-xs text-slate-500">
                          {deadline.days_remaining === 0 ? "Σήμερα" :
                           deadline.days_remaining < 0 ? `${Math.abs(deadline.days_remaining)} ημ. καθυστέρηση` :
                           `σε ${deadline.days_remaining} ημέρες`}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
              <LoadMoreFooter shown={Math.min(visible.deadlines, deadlines.length)} total={deadlines.length} testId="upcoming-deadlines"
                onMore={() => showMore("deadlines", false, deadlines.length)} onAll={() => showMore("deadlines", true, deadlines.length)} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Article 237 Info */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
              <Scale className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 mb-2">Άρθρο 237 ΚΠολΔ (Ν.5221/2025)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                <div><p className="font-medium text-slate-700">Επίδοση Αγωγής</p><p className="text-slate-500">30 ημ. (Ν.5282/2026)</p></div>
                <div><p className="font-medium text-slate-700">Προτάσεις</p><p className="text-slate-500">90 ημέρες</p></div>
                <div><p className="font-medium text-slate-700">Προσθήκη</p><p className="text-slate-500">15 ημέρες</p></div>
                <div><p className="font-medium text-slate-700">Ορισμός Δικαστή</p><p className="text-slate-500">10 ημέρες</p></div>
                <div><p className="font-medium text-slate-700">Προδικαστικός</p><p className="text-slate-500">30 ημέρες</p></div>
                <div><p className="font-medium text-amber-600">Εξαιρέσεις</p><p className="text-slate-500 text-xs">Αύγ. / Δημόσιο: 1/7-15/9</p></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
